import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import DOMPurify from "dompurify";
import { Newspaper, Trash2, Plus, X, Users, Send, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FileUpload from "@/components/FileUpload";
import AttachmentList from "@/components/AttachmentList";
import RichTextEditor from "@/components/RichTextEditor";
import TemplateSelector from "@/components/TemplateSelector";

export default function AdminDigest() {
  const [tab, setTab] = useState("compose");
  const [digests, setDigests] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [totalDigests, setTotalDigests] = useState(0);
  const [totalSubs, setTotalSubs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", content: "", tier: "basic", send_email: false, attachments: [] });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [editorKey, setEditorKey] = useState(0);

  const fetchDigests = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/digest');
      setDigests(res.data.digests);
      setTotalDigests(res.data.total);
    } catch (err) { console.error('Failed to fetch digests:', err); }
  }, []);

  const fetchSubs = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/digest/subscribers');
      setSubscribers(res.data.subscribers);
      setTotalSubs(res.data.total);
    } catch (err) { console.error('Failed to fetch subscribers:', err); }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDigests(), fetchSubs()]);
      try { const r = await apiClient.get('/admin/email/status'); setEmailStatus(r.data); } catch(e) { console.error('Failed to fetch email status:', e); }
      setLoading(false);
    };
    load();
  }, [fetchDigests, fetchSubs]);

  const handlePublish = async (e) => {
    e.preventDefault();
    setSaving(true);
    setResult(null);
    try {
      const res = await apiClient.post('/admin/digest', form);
      const emailMsg = res.data.emails_queued > 0 ? ` Emailing ${res.data.emails_queued} subscribers.` : "";
      setResult({ type: "success", msg: `Published! Reached ${res.data.subscribers_reached} subscribers.${emailMsg}` });
      setForm({ title: "", content: "", tier: "basic", send_email: false, attachments: [] });
      setEditorKey(k => k + 1);
      fetchDigests();
    } catch (err) { setResult({ type: "error", msg: "Failed to publish" }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this digest?")) return;
    try { await apiClient.delete(`/admin/digest/${id}`); fetchDigests(); }
    catch (err) { /* silent */ }
  };

  const tabs = [
    { id: "compose", label: "Compose", icon: Send },
    { id: "archive", label: "Archive", icon: Newspaper },
    { id: "subscribers", label: "Subscribers", icon: Users },
  ];

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-digest">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white mb-1">SMAHOO ME.WELL Digest</h1>
          <p className="text-white/40 text-sm">Newsletter management — {totalDigests} issues, {totalSubs} subscribers</p>
        </div>

        <div className="flex gap-2 mb-6">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all ${tab === t.id ? 'bg-amber-500/10 text-amber-400' : 'text-white/40 hover:text-white/70 hover:bg-white/5'}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            {tab === "compose" && (
              <form onSubmit={handlePublish} className="bg-[#161821] border border-white/5 rounded-2xl p-6 space-y-4" data-testid="digest-form">
                {!form.content && <TemplateSelector onSelect={(t) => { setForm({...form, title: t.name, content: t.content}); setEditorKey(k => k + 1); }} />}
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Newsletter title..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="digest-title" />
                <RichTextEditor key={editorKey} content={form.content} onChange={(html) => setForm(prev => ({...prev, content: html}))} placeholder="Write your newsletter content..." />
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    {["basic", "premium"].map(t => (
                      <button key={t} type="button" onClick={() => setForm({...form, tier: t})} className={`px-4 py-2 rounded-xl text-sm transition-all ${form.tier === t ? (t === "premium" ? "bg-violet-500/20 text-violet-400 border border-violet-500/30" : "bg-blue-500/20 text-blue-400 border border-blue-500/30") : "bg-white/5 text-white/40 border border-white/5"}`} data-testid={`tier-${t}`}>
                        {t === "premium" ? "Premium" : "Basic (Free)"}
                      </button>
                    ))}
                  </div>
                  {emailStatus?.configured && (
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="digest-send-email-toggle">
                      <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({...form, send_email: e.target.checked})} className="w-4 h-4 rounded accent-amber-500" />
                      <Mail className="w-4 h-4 text-amber-400/70" />
                      <span className="text-white/60 text-sm">Email subscribers</span>
                    </label>
                  )}
                  <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl ml-auto gap-2" data-testid="digest-publish">
                    <Send className="w-4 h-4" /> {saving ? "Publishing..." : form.send_email ? "Publish & Email" : "Publish & Send"}
                  </Button>
                </div>
                {result && <p className={`text-sm ${result.type === "success" ? "text-emerald-400" : "text-red-400"}`}>{result.msg}</p>}
                <FileUpload files={form.attachments} onChange={(files) => setForm({...form, attachments: files})} maxFiles={5} />
              </form>
            )}

            {tab === "archive" && (
              <div className="space-y-3">
                {digests.length > 0 ? digests.map(d => (
                  <div key={d.digest_id} className="bg-[#161821] border border-white/5 rounded-2xl p-5 flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${d.tier === "premium" ? "bg-violet-500/10 text-violet-400" : "bg-blue-500/10 text-blue-400"}`}>{d.tier}</span>
                        <span className="text-white/30 text-xs">{new Date(d.created_at).toLocaleDateString()}</span>
                        <span className="text-white/20 text-xs">Sent to {d.sent_count} subscribers</span>
                      </div>
                      <h3 className="text-white font-medium mb-1">{d.title}</h3>
                      <div className="text-white/50 text-sm line-clamp-2 prose prose-invert prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.content) }} />
                      <AttachmentList attachments={d.attachments} />
                    </div>
                    <button onClick={() => handleDelete(d.digest_id)} className="p-2 rounded-xl hover:bg-red-500/10 text-white/20 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )) : (
                  <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
                    <Newspaper className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/30">No newsletters published yet</p>
                  </div>
                )}
              </div>
            )}

            {tab === "subscribers" && (
              <div className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="text-white/30 text-xs uppercase border-b border-white/5">
                    <th className="text-left p-4">Name</th><th className="text-left p-4">Email</th><th className="text-left p-4">Tier</th><th className="text-left p-4">Subscribed</th>
                  </tr></thead>
                  <tbody>
                    {subscribers.length > 0 ? subscribers.map((s, i) => (
                      <tr key={s.user_id || `sub-${i}`} className="border-b border-white/5 last:border-0">
                        <td className="p-4 text-white/80">{s.name}</td>
                        <td className="p-4 text-white/50">{s.email}</td>
                        <td className="p-4"><span className={`px-2 py-0.5 rounded-full text-xs ${s.tier === "premium" ? "bg-violet-500/10 text-violet-400" : "bg-blue-500/10 text-blue-400"}`}>{s.tier}</span></td>
                        <td className="p-4 text-white/40">{new Date(s.subscribed_at).toLocaleDateString()}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="p-8 text-center text-white/30">No subscribers yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
